import { create } from 'zustand';
import { api, ApiError } from './api';

// Pulled from the shared types shape so we avoid a cross-package runtime import.
export type Role = 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'MANAGER' | 'EMPLOYEE' | 'DEMO';
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  isSuperAdmin: boolean;
  canSwitchTenants: boolean;
  tenantId: string | null;
  role: Role;
  memberships: Array<{ tenantId: string; tenantName: string; role: Role }>;
}

interface AuthState {
  user: SessionUser | null;
  loaded: boolean;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  loaded: false,
  bootstrap: async () => {
    if (get().loaded) return;
    try {
      const me = await api.get<SessionUser>('/auth/me');
      set({ user: me, loaded: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // try a refresh before giving up
        try {
          const { user } = await api.post<{ user: SessionUser }>('/auth/refresh');
          set({ user, loaded: true });
          return;
        } catch {
          /* still anonymous */
        }
      }
      set({ user: null, loaded: true });
    }
  },
  login: async (email, password) => {
    const { user } = await api.post<{ user: SessionUser }>('/auth/login', { email, password });
    set({ user });
  },
  logout: async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    set({ user: null });
  },
  switchTenant: async (tenantId) => {
    const { user } = await api.post<{ user: SessionUser }>('/auth/switch-tenant', { tenantId });
    set({ user });
  },
  refreshUser: async () => {
    const me = await api.get<SessionUser>('/auth/me');
    set({ user: me });
  },
}));
